import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Form,
  Input,
  Button,
  Checkbox,
  message,
  Modal,
  Steps,
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  MobileOutlined,
  SafetyOutlined,
  IdcardOutlined,
  MailOutlined,
} from '@ant-design/icons';

import { registerAsync } from '../../redux/slices/authSlice';
import authService from '../../services/auth';
import tianaiCaptcha from '../../services/captcha';
import './Register.css';

const { Step } = Steps;

const Register = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, isAuthenticated } = useSelector(state => state.auth);

  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [formData, setFormData] = useState({});
  const [smsSendInterval, setSmsSendInterval] = useState(60); // 默认60秒

  // 验证码相关状态
  const [captchaVisible, setCaptchaVisible] = useState(false);
  const [pendingPhone, setPendingPhone] = useState('');
  const [pendingPurpose, setPendingPurpose] = useState('');

  // 如果已经登录，重定向到首页
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // 验证码倒计时
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // 获取短信验证码配置（发送间隔等）
  useEffect(() => {
    authService.getCaptchaConfig()
      .then(res => {
        if (res?.data?.smsSendInterval) {
          setSmsSendInterval(res.data.smsSendInterval);
        }
      })
      .catch(err => {
        console.error('获取验证码配置失败:', err);
        // 使用默认值60秒
      });
  }, []);

  // 发送验证码
  const handleSendCode = async () => {
    try {
      const phone = form.getFieldValue('phone');
      if (!phone) {
        message.error('请输入手机号');
        return;
      }

      // 调用发送验证码的API
      const res = await authService.sendVerificationCode(phone, 'register');
      const data = res?.data;
      if (data?.captchaRequired && !data?.sent) {
        // 需要完成人机验证，打开验证码弹窗
        setPendingPhone(phone);
        setPendingPurpose('register');
        setCaptchaVisible(true);
        message.warning(res?.message || '需要完成人机验证');
        return;
      }
      message.success('验证码已发送');
      setCountdown(smsSendInterval);
    } catch (error) {
      message.error(error.message || '发送验证码失败');
    }
  };

  // 处理验证码验证成功
  const handleCaptchaSuccess = async (token) => {
    try {
      // 使用验证码token发送短信验证码
      const res = await authService.sendVerificationCode(pendingPhone, pendingPurpose, token);
      const responseData = res?.data;

      if (responseData?.sent) {
        message.success('验证码已发送');
        setCountdown(smsSendInterval);
        setCaptchaVisible(false);
      } else {
        message.error(responseData?.message || '发送验证码失败');
      }
    } catch (error) {
      message.error(error?.message || '发送验证码失败');
    }
  };

  // 第一步：手机号验证
  const handleStepOne = async (values) => {
    try {
      // 验证手机号和验证码（注册用途）
      const result = await authService.verifyCode(values.phone, values.verificationCode, 'register');

      // 检查验证结果
      if (!result.data) {
        message.error('验证码错误或已过期');
        return;
      }

      setFormData({ ...formData, ...values });
      setCurrentStep(1);
      message.success('手机号验证成功');
    } catch (error) {
      message.error(error.message || '验证失败');
    }
  };

  // 第二步：设置账号信息
  const handleStepTwo = async (values) => {
    try {
      const registerData = {
        ...formData,
        ...values,
      };

      await dispatch(registerAsync(registerData)).unwrap();

      message.success('注册成功，正在跳转...');
      setTimeout(() => {
        navigate('/auth/login', { replace: true });
      }, 1500);
    } catch (error) {
      message.error(error.message || '注册失败');
    }
  };

  // 返回上一步
  const handlePrevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const renderStepOne = () => (
    <Form
      form={form}
      name="register-step-one"
      onFinish={handleStepOne}
      autoComplete="off"
      size="middle"
      initialValues={formData}
    >
      <Form.Item
        name="phone"
        rules={[
          { required: true, message: '请输入手机号' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' },
        ]}
      >
        <Input
          prefix={<MobileOutlined />}
          placeholder="手机号"
          autoComplete="tel"
        />
      </Form.Item>

      <Form.Item
        name="verificationCode"
        rules={[
          { required: true, message: '请输入验证码' },
          { len: 6, message: '验证码为6位数字' },
        ]}
      >
        <div className="verification-input">
          <Input
            prefix={<SafetyOutlined />}
            placeholder="验证码"
            autoComplete="one-time-code"
          />
          <Button
            type="default"
            onClick={handleSendCode}
            disabled={countdown > 0}
            className="send-code-button"
            size="middle"
          >
            {countdown > 0 ? `${countdown}s` : '发送验证码'}
          </Button>
        </div>
      </Form.Item>

      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          block
          className="register-button"
          size="middle"
        >
          下一步
        </Button>
      </Form.Item>
    </Form>
  );

  const renderStepTwo = () => (
    <Form
      form={form}
      name="register-step-two"
      onFinish={handleStepTwo}
      autoComplete="off"
      size="middle"
      initialValues={formData}
    >
      <Form.Item
        name="username"
        rules={[
          { required: true, message: '请输入用户名' },
          { min: 2, max: 20, message: '用户名长度为2-20个字符' },
          { pattern: /^[a-zA-Z0-9_\u4e00-\u9fa5]+$/, message: '用户名只能包含字母、数字、下划线和中文' },
        ]}
      >
        <Input
          prefix={<UserOutlined />}
          placeholder="用户名"
          autoComplete="username"
        />
      </Form.Item>

      <Form.Item
        name="realName"
        rules={[
          { required: true, message: '请输入真实姓名' },
          { min: 2, max: 10, message: '真实姓名长度为2-10个字符' },
          { pattern: /^[\u4e00-\u9fa5]+$/, message: '真实姓名只能包含中文' },
        ]}
      >
        <Input
          prefix={<IdcardOutlined />}
          placeholder="真实姓名"
          autoComplete="name"
        />
      </Form.Item>

      <Form.Item
        name="email"
        rules={[
          { type: 'email', message: '请输入正确的邮箱地址' },
          { max: 100, message: '邮箱长度不能超过100个字符' },
        ]}
      >
        <Input
          prefix={<MailOutlined />}
          placeholder="邮箱（选填）"
          autoComplete="email"
        />
      </Form.Item>

      <Form.Item
        name="password"
        rules={[
          { required: true, message: '请输入密码' },
          { min: 6, max: 20, message: '密码长度为6-20个字符' },
          { pattern: /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]+$/, message: '密码必须包含字母和数字' },
        ]}
      >
        <Input.Password
          prefix={<LockOutlined />}
          placeholder="密码（包含字母和数字）"
          autoComplete="new-password"
        />
      </Form.Item>

      <Form.Item
        name="confirmPassword"
        dependencies={['password']}
        rules={[
          { required: true, message: '请确认密码' },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error('两次输入的密码不一致'));
            },
          }),
        ]}
      >
        <Input.Password
          prefix={<LockOutlined />}
          placeholder="确认密码"
          autoComplete="new-password"
        />
      </Form.Item>

      <Form.Item
        name="agreement"
        valuePropName="checked"
        rules={[
          { validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error('请同意用户协议')) },
        ]}
      >
        <Checkbox>
          我已阅读并同意
          <Link to="/terms" target="_blank">《用户协议》</Link>
          和
          <Link to="/privacy" target="_blank">《隐私政策》</Link>
        </Checkbox>
      </Form.Item>

      <Form.Item>
        <div className="register-buttons">
          <Button
            type="default"
            onClick={handlePrevStep}
            className="prev-button"
            size="middle"
          >
            上一步
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            className="register-button"
            size="middle"
          >
            注册
          </Button>
        </div>
      </Form.Item>
    </Form>
  );

  // 当验证码弹窗打开时，初始化验证码
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!captchaVisible) return;

    let disposed = false;
    const timer = setTimeout(() => {
      if (disposed) return;
      tianaiCaptcha.show({
        bindEl: '#tianai-captcha-box',
        onValidSuccess: (token, tac) => {
          handleCaptchaSuccess(token);
          tac.destroyWindow();
          setCaptchaVisible(false);
        },
        onValidFail: () => {
          message.error('验证失败，请重试');
        }
      }).catch((error) => {
        // console.error('验证码初始化失败:', error);
        message.error(error?.message || '验证码初始化失败');
        setCaptchaVisible(false);
      });
    }, 0);

    return () => {
      disposed = true;
      clearTimeout(timer);
      tianaiCaptcha.destroy();
    };
  }, [captchaVisible]);

  return (
    <div className="register-container">
      <Steps current={currentStep} className="register-steps" size="small">
        <Step title="手机验证" description="验证手机号" />
        <Step title="设置账号" description="完善账号信息" />
      </Steps>

      <div className="register-form">
        {currentStep === 0 && renderStepOne()}
        {currentStep === 1 && renderStepTwo()}
      </div>

      <div className="login-link">
        已有账号？
        <Link to="/auth/login">立即登录</Link>
      </div>

      {/* 验证码弹窗 */}
      <Modal
        open={captchaVisible}
        title="安全验证"
        onCancel={() => setCaptchaVisible(false)}
        footer={null}
        destroyOnClose
        centered
      >
        <div id="tianai-captcha-box" style={{ minWidth: 318, minHeight: 318 }} />
      </Modal>
    </div>
  );
};

export default Register;
